import type { BranchPermission, ModuleKey } from '../../../../shell/session/store/sessionTypes';
import { actionLabelEs, customizableActionsForModule } from '../../../../shell/session/permissions/permissionCatalog';

export const MODULE_LABELS_ES: Record<ModuleKey, string> = {
  POS: 'POS',
  Catalog: 'Catálogo',
  Inventory: 'Stock',
  Customers: 'Clientes',
  Reports: 'Informes',
  BranchSettings: 'Ajustes',
  Promotions: 'Promos',
  PriceLists: 'Listas',
  Loyalty: 'Fidelización',
  Expenses: 'Gastos',
  Purchases: 'Compras',
  Agenda: 'Agenda',
  WorkOrders: 'Órdenes de trabajo',
  Commissions: 'Comisiones',
  Cash: 'Caja',
  Checks: 'Cheques',
  Orders: 'Pedidos',
  Tables: 'Mesas',
  Kitchen: 'Cocina',
  MenuOptions: 'Opciones de menú',
  Team: 'Equipo',
  Organization: 'Organización',
  Billing: 'Plan y facturación',
  Support: 'Soporte',
};

export const MODULE_SHORT_LABELS_ES: Partial<Record<ModuleKey, string>> = {
  POS: 'POS',
  Catalog: 'Cat',
  Inventory: 'Stk',
  Customers: 'Cli',
  Reports: 'Inf',
  BranchSettings: 'Aj',
  Promotions: 'Prom',
  Expenses: 'Gast',
  Purchases: 'Comp',
  Agenda: 'Ag.',
  Cash: 'Caja',
  Checks: 'Chq',
  Orders: 'Ped',
  Tables: 'Mesas',
  Kitchen: 'Coc',
  MenuOptions: 'Menú',
  Team: 'Eq',
  Organization: 'Org',
  Billing: 'Plan',
  Support: 'Sop',
};

function levelPhrase(perm: BranchPermission): string | null {
  if (!perm.canView) return null;
  const name = MODULE_LABELS_ES[perm.module] ?? perm.module;
  if (!perm.canEdit) return `ver ${name}`;
  const bits: string[] = [`usar ${name}`];
  if (perm.canVoid) bits.push('anular');
  if (perm.canDiscount) bits.push('descontar');
  if (perm.module === 'Agenda' && perm.canManageAgenda) bits.push('manejar agenda');
  if (perm.module === 'Agenda' && perm.canViewOthers === false) bits.push('solo su agenda');
  return bits.join(', ');
}

/**
 * Short Spanish “Puede… / No puede…” blurb for the employee detail sheet.
 */
export function buildEffectivePermissionSummary(
  permissions: BranchPermission[],
  visibleModules: ModuleKey[]
): { can: string; cannot: string } {
  const visible = new Set(visibleModules);
  const active = permissions.filter((p) => visible.has(p.module));

  const canParts: string[] = [];
  const cannotModules: string[] = [];

  for (const perm of active) {
    const phrase = levelPhrase(perm);
    if (!phrase) {
      cannotModules.push(MODULE_LABELS_ES[perm.module] ?? perm.module);
      continue;
    }
    canParts.push(phrase);

    const customOn = customizableActionsForModule(perm.module).filter(
      (a) => perm.actions?.[a] === true
    );
    if (customOn.length > 0 && customOn.length <= 3) {
      canParts.push(customOn.map(actionLabelEs).join(', ').toLowerCase());
    }
  }

  // Keep blurb short: first 4 cans, first 4 cannots.
  const can =
    canParts.length === 0
      ? 'No puede usar ningún módulo.'
      : `Puede ${canParts.slice(0, 4).join('; ')}${canParts.length > 4 ? '…' : ''}.`;

  const cannot =
    cannotModules.length === 0
      ? 'Tiene acceso en todos los módulos.'
      : `No puede usar ${cannotModules.slice(0, 5).join(', ')}${
          cannotModules.length > 5 ? '…' : ''
        }.`;

  return { can, cannot };
}

/** Resumen corto de módulos con acceso (lista / filas). */
export function summarizeEmployeeModuleAccess(
  employee: {
    branchAssignments: Array<{ branchId: string; permissions: BranchPermission[] }>;
  },
  visibleModules: readonly ModuleKey[],
  branchFilter?: string | null
): string {
  const visible = new Set(visibleModules);
  const modulesWithAccess = new Set<ModuleKey>();

  for (const assignment of employee.branchAssignments) {
    if (branchFilter && assignment.branchId !== branchFilter) continue;
    for (const perm of assignment.permissions) {
      if (perm.canView && visible.has(perm.module)) {
        modulesWithAccess.add(perm.module);
      }
    }
  }

  const labels = [...modulesWithAccess]
    .map((m) => MODULE_LABELS_ES[m] ?? m)
    .sort((a, b) => a.localeCompare(b, 'es'));

  if (labels.length === 0) return 'Sin acceso a módulos';

  const top = labels.slice(0, 3);
  const extra = labels.length - top.length;
  return extra > 0 ? `${top.join(', ')} +${extra}` : top.join(', ');
}
